/*
 * Toy grammar for the HomeChores mock. A keyword matcher over a small token table,
 * plus four canonical sentences bundled in four languages with their parsed cards,
 * so the suggestion pills always parse perfectly. Invented first names only.
 */

export type Locale = 'en' | 'pt' | 'es' | 'fr';
export type CardKind = 'task' | 'shopping' | 'event' | 'bill';
export type Recurrence = 'daily' | 'weekly' | 'monthly';
export type Relative = 'today' | 'tomorrow' | 'tonight' | 'next';
export type KeyArg = 'when' | 'amount' | 'assignee' | 'title';

export interface CardSeed {
  kind: CardKind;
  /* Typed text, or the English title for a bundled card. */
  title: string;
  /* Bundled cards carry every locale so a language switch re-renders the same card. */
  titleByLocale?: Record<Locale, string>;
  /* 0 = Sunday .. 6 = Saturday. */
  weekday?: number;
  relative?: Relative;
  recurrence?: Recurrence;
  /* Minutes from midnight. */
  time?: number;
  amount?: number;
  quantity?: number;
  assignee?: string;
  /* A raw "when" typed into the edit field that the grammar could not parse. */
  whenText?: string;
  keyArg: KeyArg;
}

export interface CanonicalSentence {
  id: string;
  sentence: Record<Locale, string>;
  cards: CardSeed[];
}

export interface ParseResult {
  unmatched: boolean;
  cards: CardSeed[];
}

export const LOCALES: Locale[] = ['en', 'pt', 'es', 'fr'];

export const LOCALE_TAGS: Record<Locale, string> = {
  en: 'en-US',
  pt: 'pt-BR',
  es: 'es-ES',
  fr: 'fr-FR',
};

export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  pt: 'Portuguese',
  es: 'Spanish',
  fr: 'French',
};

export const KIND_LABELS: Record<Locale, Record<CardKind, string>> = {
  en: { task: 'TASK', shopping: 'SHOPPING', event: 'EVENT', bill: 'BILL' },
  pt: { task: 'TAREFA', shopping: 'COMPRAS', event: 'EVENTO', bill: 'CONTA' },
  es: { task: 'TAREA', shopping: 'COMPRAS', event: 'EVENTO', bill: 'FACTURA' },
  fr: { task: 'TÂCHE', shopping: 'COURSES', event: 'ÉVÉNEMENT', bill: 'FACTURE' },
};

/* ------------------------------------------------------------------ phrases */

interface Phrases {
  weekdays: string[];
  everyWeekday: string[];
  oneWeekday: string;
  nextWeekday: string;
  daily: string;
  weekly: string;
  monthly: string;
  today: string;
  tomorrow: string;
  tonight: string;
  at: string;
  assignee: string;
  currency: string;
}

const PHRASES: Record<Locale, Phrases> = {
  en: {
    weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    everyWeekday: [
      'every Sunday',
      'every Monday',
      'every Tuesday',
      'every Wednesday',
      'every Thursday',
      'every Friday',
      'every Saturday',
    ],
    oneWeekday: '{d}',
    nextWeekday: 'next {d}',
    daily: 'every day',
    weekly: 'every week',
    monthly: 'every month',
    today: 'today',
    tomorrow: 'tomorrow',
    tonight: 'tonight',
    at: 'at {t}',
    assignee: 'for {n}',
    currency: 'USD',
  },
  pt: {
    weekdays: ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'],
    everyWeekday: [
      'todo domingo',
      'toda segunda',
      'toda terça',
      'toda quarta',
      'toda quinta',
      'toda sexta',
      'todo sábado',
    ],
    oneWeekday: '{d}',
    nextWeekday: '{d} que vem',
    daily: 'todo dia',
    weekly: 'toda semana',
    monthly: 'todo mês',
    today: 'hoje',
    tomorrow: 'amanhã',
    tonight: 'hoje à noite',
    at: 'às {t}',
    assignee: 'para {n}',
    currency: 'BRL',
  },
  es: {
    weekdays: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    everyWeekday: [
      'cada domingo',
      'cada lunes',
      'cada martes',
      'cada miércoles',
      'cada jueves',
      'cada viernes',
      'cada sábado',
    ],
    oneWeekday: 'el {d}',
    nextWeekday: 'el {d} que viene',
    daily: 'cada día',
    weekly: 'cada semana',
    monthly: 'cada mes',
    today: 'hoy',
    tomorrow: 'mañana',
    tonight: 'esta noche',
    at: 'a las {t}',
    assignee: 'para {n}',
    currency: 'EUR',
  },
  fr: {
    weekdays: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'],
    everyWeekday: [
      'chaque dimanche',
      'chaque lundi',
      'chaque mardi',
      'chaque mercredi',
      'chaque jeudi',
      'chaque vendredi',
      'chaque samedi',
    ],
    oneWeekday: '{d}',
    nextWeekday: '{d} prochain',
    daily: 'chaque jour',
    weekly: 'chaque semaine',
    monthly: 'chaque mois',
    today: "aujourd'hui",
    tomorrow: 'demain',
    tonight: 'ce soir',
    at: 'à {t}',
    assignee: 'pour {n}',
    currency: 'EUR',
  },
};

/* ------------------------------------------------------------- canonical */

const t = (en: string, pt: string, es: string, fr: string): Record<Locale, string> => ({
  en,
  pt,
  es,
  fr,
});

export const CANONICAL: CanonicalSentence[] = [
  {
    id: 'milk-bill',
    sentence: t(
      'Buy milk and pay the electric bill Friday',
      'Comprar leite e pagar a conta de luz sexta',
      'Comprar leche y pagar la factura de la luz el viernes',
      "Acheter du lait et payer la facture d'électricité vendredi"
    ),
    cards: [
      {
        kind: 'shopping',
        title: 'Milk',
        titleByLocale: t('Milk', 'Leite', 'Leche', 'Lait'),
        keyArg: 'title',
      },
      {
        kind: 'bill',
        title: 'Electric bill',
        titleByLocale: t(
          'Electric bill',
          'Conta de luz',
          'Factura de la luz',
          "Facture d'électricité"
        ),
        weekday: 5,
        keyArg: 'when',
      },
    ],
  },
  {
    id: 'soccer',
    sentence: t(
      'Soccer practice Tuesday 6pm, remind Ana',
      'Treino de futebol terça às 18h, lembrar a Ana',
      'Entrenamiento de fútbol el martes a las 18h, avisar a Ana',
      'Entraînement de foot mardi à 18h, rappeler à Ana'
    ),
    cards: [
      {
        kind: 'event',
        title: 'Soccer practice',
        titleByLocale: t(
          'Soccer practice',
          'Treino de futebol',
          'Entrenamiento de fútbol',
          'Entraînement de foot'
        ),
        weekday: 2,
        time: 18 * 60,
        keyArg: 'when',
      },
      {
        kind: 'task',
        title: 'Soccer practice reminder',
        titleByLocale: t(
          'Soccer practice reminder',
          'Lembrete do treino de futebol',
          'Recordatorio del entrenamiento',
          "Rappel de l'entraînement"
        ),
        weekday: 2,
        time: 18 * 60,
        assignee: 'Ana',
        keyArg: 'assignee',
      },
    ],
  },
  {
    id: 'trash',
    sentence: t(
      'Take the trash out every Sunday at 8pm',
      'Levar o lixo para fora todo domingo às 20h',
      'Sacar la basura cada domingo a las 20h',
      'Sortir les poubelles chaque dimanche à 20h'
    ),
    cards: [
      {
        kind: 'task',
        title: 'Take the trash out',
        titleByLocale: t(
          'Take the trash out',
          'Levar o lixo para fora',
          'Sacar la basura',
          'Sortir les poubelles'
        ),
        weekday: 0,
        recurrence: 'weekly',
        time: 20 * 60,
        keyArg: 'when',
      },
    ],
  },
  {
    id: 'vet',
    sentence: t(
      'Vet appointment for Mel next Thursday 3pm',
      'Consulta no veterinário para a Mel quinta que vem às 15h',
      'Cita en el veterinario para Mel el jueves que viene a las 15h',
      'Rendez-vous chez le vétérinaire pour Mel jeudi prochain à 15h'
    ),
    cards: [
      {
        kind: 'event',
        title: 'Vet appointment for Mel',
        titleByLocale: t(
          'Vet appointment for Mel',
          'Consulta no veterinário para a Mel',
          'Cita en el veterinario para Mel',
          'Rendez-vous vétérinaire pour Mel'
        ),
        weekday: 4,
        relative: 'next',
        time: 15 * 60,
        keyArg: 'when',
      },
    ],
  },
];

/* Two items the family already has, so the screen and the feed are never blank. */
export const SEED_CARDS: { card: CardSeed; initial: string }[] = [
  {
    card: {
      kind: 'task',
      title: 'Water the plants',
      titleByLocale: t(
        'Water the plants',
        'Regar as plantas',
        'Regar las plantas',
        'Arroser les plantes'
      ),
      weekday: 3,
      keyArg: 'when',
    },
    initial: 'J',
  },
  {
    card: {
      kind: 'shopping',
      title: 'Coffee',
      titleByLocale: t('Coffee', 'Café', 'Café', 'Café'),
      keyArg: 'title',
    },
    initial: 'A',
  },
];

/* --------------------------------------------------------------- tokens */

type TokenKind = 'verb' | 'day' | 'rel' | 'every' | 'unit' | 'recur' | 'person' | 'noise';

interface Token {
  w: string;
  k: TokenKind;
  v: string;
}

const tok = (k: TokenKind, v: string, words: string): Token[] =>
  words.split('|').map(w => ({ w, k, v }));

const TOKENS: Token[] = [
  /* verbs, value = card kind. 'task' verbs stay in the title (Take the trash out); 'taskmeta' and 'remind' are stripped. */
  ...tok('verb', 'shopping', 'buy|get|pick up|add|put|we need|need'),
  ...tok('verb', 'shopping', 'comprar|compra|adiciona|adicionar|coloca|precisamos de'),
  ...tok('verb', 'shopping', 'añade|añadir|agrega|agregar'),
  ...tok('verb', 'shopping', 'acheter|achète|ajoute|ajouter'),
  ...tok('verb', 'bill', 'pay|pagar|paga|payer|paie'),
  ...tok('verb', 'remind', 'remind me|remind'),
  ...tok('verb', 'remind', 'me lembra|me lembre|lembrar|lembra|lembre'),
  ...tok('verb', 'remind', 'recuérdame|recuerda|recordar|avísame|avisa|avisar'),
  ...tok('verb', 'remind', 'rappelle-moi|rappelle|rappeler'),
  ...tok('verb', 'event', 'schedule|book|appointment'),
  ...tok('verb', 'event', 'agenda|agendar|marca|marcar|consulta'),
  ...tok('verb', 'event', 'programa|cita'),
  ...tok('verb', 'event', 'planifie|planifier|prévois|rendez-vous'),
  ...tok('verb', 'task', 'take|clean|wash|call|water|walk|fix'),
  ...tok('verb', 'taskmeta', 'create a task to|create a task|create the task to|create the task'),
  ...tok('verb', 'taskmeta', 'create a routine to|make a task to|add a task to'),
  ...tok('verb', 'task', 'levar|leva|tirar|tira|limpar|limpa|lavar|regar'),
  ...tok(
    'verb',
    'taskmeta',
    'cria tarefa de|cria a tarefa de|cria uma rotina de|cria uma tarefa de'
  ),
  ...tok('verb', 'task', 'sacar|saca|llevar|limpiar'),
  ...tok('verb', 'taskmeta', 'crea una tarea para|crea una tarea'),
  ...tok('verb', 'task', 'sortir|sors|nettoyer|nettoie|laver|arroser'),
  ...tok('verb', 'taskmeta', 'crée une tâche pour|crée une tâche'),
  /* weekdays, value = 0..6 */
  ...tok('day', '0', 'sunday|domingo|dimanche'),
  ...tok('day', '1', 'monday|segunda-feira|segunda|lunes|lundi'),
  ...tok('day', '2', 'tuesday|terça-feira|terça|martes|mardi'),
  ...tok('day', '3', 'wednesday|quarta-feira|quarta|miércoles|mercredi'),
  ...tok('day', '4', 'thursday|quinta-feira|quinta|jueves|jeudi'),
  ...tok('day', '5', 'friday|sexta-feira|sexta|viernes|vendredi'),
  ...tok('day', '6', 'saturday|sábado|samedi'),
  /* relative words */
  ...tok('rel', 'today', "today|hoje|hoy|aujourd'hui"),
  ...tok('rel', 'tomorrow', 'tomorrow|amanhã|mañana|demain'),
  ...tok('rel', 'tonight', 'tonight|hoje à noite|esta noche|ce soir'),
  ...tok('rel', 'next', 'next|que vem|próxima|próximo|que viene|prochain|prochaine'),
  /* recurrence: an "every" word followed by a weekday or a unit */
  ...tok('every', 'every', 'every|each|todo|toda|todos|todas|cada|todos los|todas las'),
  ...tok('every', 'every', 'chaque|tous les|toutes les'),
  ...tok('unit', 'daily', 'day|night|morning|evening|dia|noite|manhã|día|noche|jour|soir|matin'),
  ...tok('unit', 'weekly', 'week|semana|semaine'),
  ...tok('unit', 'monthly', 'month|mês|mes|mois'),
  ...tok('recur', 'daily', 'daily|diariamente|quotidiennement'),
  ...tok('recur', 'weekly', 'weekly|semanalmente|hebdomadairement'),
  ...tok('recur', 'monthly', 'monthly|mensalmente|mensualmente|mensuellement'),
  /* invented first names */
  ...tok('person', '', 'ana|mel|bia|leo|nina|theo'),
  /* list phrases that carry no meaning of their own */
  ...tok('noise', '', 'to the shopping list|to the grocery list|on the shopping list'),
  ...tok('noise', '', 'on the grocery list|to the list|on the list|to the cart'),
  ...tok('noise', '', 'na lista de compras|na lista|nas compras|a la lista de compras'),
  ...tok('noise', '', 'a la lista|en la lista|à la liste de courses|à la liste|sur la liste'),
];

const LEADING_STOP = new Set([
  'the',
  'a',
  'an',
  'my',
  'me',
  'to',
  'o',
  'os',
  'as',
  'um',
  'uma',
  'meu',
  'minha',
  'de',
  'do',
  'da',
  'el',
  'la',
  'los',
  'las',
  'un',
  'una',
  'mi',
  'le',
  'les',
  'du',
  'des',
  'une',
  'mon',
  'ma',
  'mes',
  'que',
  'pra',
  'about',
  'sobre',
  'del',
]);

const TRAILING_STOP = new Set([
  'at',
  'on',
  'in',
  'às',
  'as',
  'a',
  'à',
  'em',
  'en',
  'le',
  'el',
  'no',
  'na',
  'the',
  'de',
  'du',
  'por',
  'pour',
  'para',
  'for',
  'and',
  'e',
  'y',
  'et',
]);

const QUESTION_START =
  /^(what|what's|whats|which|how|when|where|who|o que|quais|qual|como|quando|onde|qué|que|cuáles|cuál|cómo|cuándo|quoi|quel|quelle|quels|comment|quand|combien)\b/;

/* ------------------------------------------------------------- matching */

/* Lowercase and strip accents while keeping every code point in place, so spans map back to the original. */
export function normalize(text: string): string {
  return Array.from(text.toLowerCase())
    .map(ch => (ch.length > 1 ? ch : ch.normalize('NFD')[0]))
    .join('');
}

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

interface Compiled {
  token: Token;
  re: RegExp;
}

const COMPILED: Compiled[] = TOKENS.map(token => ({
  token,
  re: new RegExp(`(?:^|[\\s,;.!?])(${escapeRe(normalize(token.w))})(?=$|[\\s,;.!?:])`, 'g'),
})).sort((a, b) => b.token.w.length - a.token.w.length);

interface Hit {
  token: Token;
  start: number;
  end: number;
}

interface Span {
  start: number;
  end: number;
}

const overlaps = (a: Span, b: Span): boolean => a.start < b.end && b.start < a.end;

function findHits(norm: string): Hit[] {
  const hits: Hit[] = [];
  COMPILED.forEach(({ token, re }) => {
    re.lastIndex = 0;
    let m = re.exec(norm);
    while (m) {
      const start = m.index + m[0].indexOf(m[1]);
      const hit = { token, start, end: start + m[1].length };
      if (!hits.some(h => overlaps(h, hit))) hits.push(hit);
      m = re.exec(norm);
    }
  });
  return hits.sort((a, b) => a.start - b.start);
}

const TIME_RE =
  /(?:^|[\s,])(?:(a las|a la|at|as|a|à)\s+)?(\d{1,2})(?::(\d{2}))?\s?(am|pm|h|hs)?(\d{2})?(?=$|[\s,;.!?])/g;

const AMOUNT_RE =
  /(?:(?:us\$|r\$|\$|€|£)\s?(\d+(?:[.,]\d{1,2})?))|(?:(\d+(?:[.,]\d{1,2})?)\s?(?:dollars|bucks|reais|euros|usd|brl|eur|€|\$))/g;

interface WhenResult {
  weekday?: number;
  relative?: Relative;
  recurrence?: Recurrence;
  time?: number;
  spans: Span[];
}

function readWhen(norm: string, hits: Hit[]): WhenResult {
  const out: WhenResult = { spans: [] };
  const consumed = new Set<Hit>();

  hits.forEach((hit, i) => {
    if (hit.token.k !== 'every') return;
    const next = hits[i + 1];
    if (!next || next.start - hit.end > 2) return;
    if (next.token.k === 'day') {
      out.recurrence = 'weekly';
      out.weekday = Number(next.token.v);
      consumed.add(next);
    } else if (next.token.k === 'unit') {
      out.recurrence = next.token.v as Recurrence;
      consumed.add(next);
    } else {
      return;
    }
    consumed.add(hit);
    out.spans.push({ start: hit.start, end: next.end });
  });

  hits.forEach(hit => {
    if (consumed.has(hit)) return;
    if (hit.token.k === 'day' && out.weekday === undefined) {
      out.weekday = Number(hit.token.v);
      out.spans.push(hit);
    } else if (hit.token.k === 'rel') {
      const rel = hit.token.v as Relative;
      if (rel !== 'next' || out.weekday !== undefined || hits.some(h => h.token.k === 'day')) {
        out.relative = rel;
        out.spans.push(hit);
      }
    } else if (hit.token.k === 'recur') {
      out.recurrence = hit.token.v as Recurrence;
      out.spans.push(hit);
    }
  });

  TIME_RE.lastIndex = 0;
  let m = TIME_RE.exec(norm);
  while (m) {
    const [whole, prefix, hh, mm, marker, trailing] = m;
    const explicit = Boolean(prefix || mm || marker);
    if (explicit) {
      let hour = Number(hh);
      const minutes = Number(mm || (marker === 'h' || marker === 'hs' ? trailing : '') || 0);
      if (marker === 'pm' && hour < 12) hour += 12;
      if (marker === 'am' && hour === 12) hour = 0;
      if (!marker && hour >= 1 && hour <= 6) hour += 12;
      if (hour < 24 && minutes < 60) {
        out.time = hour * 60 + minutes;
        const start = m.index + (/^[\s,]/.test(whole) ? 1 : 0);
        out.spans.push({ start, end: m.index + whole.length });
        break;
      }
    }
    m = TIME_RE.exec(norm);
  }

  return out;
}

function readAmount(norm: string): { amount?: number; span?: Span } {
  AMOUNT_RE.lastIndex = 0;
  const m = AMOUNT_RE.exec(norm);
  if (!m) return {};
  const raw = (m[1] || m[2] || '').replace(',', '.');
  const amount = Number(raw);
  if (Number.isNaN(amount)) return {};
  return { amount, span: { start: m.index, end: m.index + m[0].length } };
}

function cut(text: string, spans: Span[]): string {
  const keep = text
    .split('')
    .map((ch, i) => (spans.some(s => i >= s.start && i < s.end) ? ' ' : ch));
  return keep.join('');
}

function tidyTitle(raw: string): string {
  const words = raw
    .replace(/[,;.!?]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  while (words.length && LEADING_STOP.has(normalize(words[0]))) {
    /* English "do the dishes" keeps its verb; Portuguese "do" (de + o) is an article. */
    if (normalize(words[0]) === 'do' && words[1] && normalize(words[1]) === 'the') break;
    words.shift();
  }
  while (words.length && TRAILING_STOP.has(normalize(words[words.length - 1]))) words.pop();
  const joined = words.join(' ').trim();
  return joined ? joined.charAt(0).toUpperCase() + joined.slice(1) : '';
}

function pickKeyArg(card: Omit<CardSeed, 'keyArg'>): KeyArg {
  if (card.amount !== undefined) return 'amount';
  if (card.assignee) return 'assignee';
  if (card.weekday !== undefined || card.relative || card.recurrence || card.time !== undefined)
    return 'when';
  return 'title';
}

const verbKind = (v: string): CardKind =>
  v === 'remind' || v === 'taskmeta' ? 'task' : (v as CardKind);

function parseClause(
  clause: string,
  inheritedKind: CardKind | null,
  previousTitle: string
): CardSeed | null {
  const norm = normalize(clause);
  const hits = findHits(norm);
  const verb = hits.find(h => h.token.k === 'verb');
  if (!verb && !inheritedKind) return null;

  const spans: Span[] = [];
  const kind: CardKind = verb ? verbKind(verb.token.v) : (inheritedKind as CardKind);
  if (verb && verb.token.v !== 'task') spans.push(verb);
  hits.filter(h => h.token.k === 'noise').forEach(h => spans.push(h));

  let assignee: string | undefined;
  if (verb && verb.token.v === 'remind') {
    const person = hits.find(
      h =>
        h.token.k === 'person' &&
        h.start >= verb.end &&
        /^[\s]*(a|à|o|la|el|le|to|para|pour)?[\s]*$/.test(norm.slice(verb.end, h.start))
    );
    if (person) {
      assignee = clause.slice(person.start, person.end);
      assignee = assignee.charAt(0).toUpperCase() + assignee.slice(1);
      spans.push({ start: verb.end, end: person.end });
    }
  }

  const when = readWhen(norm, hits);
  spans.push(...when.spans);
  const money = readAmount(norm);
  if (money.span) spans.push(money.span);

  let title = tidyTitle(cut(clause, spans));
  let quantity: number | undefined;
  if (kind === 'shopping') {
    const q = /^(\d+)\s*(?:x\s*)?(.+)$/.exec(title);
    if (q) {
      quantity = Number(q[1]);
      title = q[2].charAt(0).toUpperCase() + q[2].slice(1);
    }
  }
  if (!title) title = previousTitle || tidyTitle(clause) || clause.trim();

  const card: Omit<CardSeed, 'keyArg'> = {
    kind,
    title,
    weekday: when.weekday,
    relative: when.relative,
    recurrence: when.recurrence,
    time: when.time,
    amount: money.amount,
    quantity,
    assignee,
  };
  return { ...card, keyArg: pickKeyArg(card) };
}

const CLAUSE_SPLIT = /\s*(?:[,;]|\s(?:and|e|y|et)\s)\s*/i;

export function findCanonical(text: string): CanonicalSentence | undefined {
  const key = normalize(text)
    .replace(/[.!?]+$/, '')
    .trim();
  return CANONICAL.find(c =>
    LOCALES.some(
      l =>
        normalize(c.sentence[l])
          .replace(/[.!?]+$/, '')
          .trim() === key
    )
  );
}

/* Free text: split into clauses, match verbs and arguments, emit 1 to 4 cards. */
export function parseSentence(text: string): ParseResult {
  const trimmed = text.trim();
  const canonical = findCanonical(trimmed);
  if (canonical) return { unmatched: false, cards: canonical.cards };

  const norm = normalize(trimmed);
  const isQuestion = /\?\s*$/.test(trimmed) || QUESTION_START.test(norm);
  const cards: CardSeed[] = [];

  if (!isQuestion) {
    let kind: CardKind | null = null;
    let previousTitle = '';
    trimmed.split(CLAUSE_SPLIT).forEach(clause => {
      if (!clause.trim() || cards.length >= 4) return;
      const card = parseClause(clause, kind, previousTitle);
      if (!card) return;
      kind = card.kind;
      previousTitle = card.title;
      cards.push(card);
    });
  }

  if (cards.length) return { unmatched: false, cards };

  /* The honest fallback: file the raw sentence as a task and say so. */
  const raw = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return { unmatched: true, cards: [{ kind: 'task', title: raw, keyArg: 'when' }] };
}

/* Re-read a typed "when" from the edit field, in any language. */
export function readWhenText(text: string): Omit<WhenResult, 'spans'> {
  const norm = normalize(text);
  const { spans, ...rest } = readWhen(norm, findHits(norm));
  return spans.length ? rest : {};
}

/* ------------------------------------------------------------ rendering */

const pad = (n: number): string => (n < 10 ? `0${n}` : String(n));

export function formatTime(minutes: number, locale: Locale): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (locale === 'en') {
    const suffix = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return m ? `${h12}:${pad(m)}${suffix}` : `${h12}${suffix}`;
  }
  return m ? `${h}h${pad(m)}` : `${h}h`;
}

export function formatAmount(amount: number, locale: Locale): string {
  return new Intl.NumberFormat(LOCALE_TAGS[locale], {
    style: 'currency',
    currency: PHRASES[locale].currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function cardTitle(card: CardSeed, locale: Locale): string {
  return card.titleByLocale ? card.titleByLocale[locale] : card.title;
}

/* The "when" phrase alone: 'every Sunday at 8pm', 'todo domingo às 20h'. Empty when the card has no time. */
export function cardWhen(card: CardSeed, locale: Locale): string {
  if (card.whenText) return card.whenText;
  const p = PHRASES[locale];
  const parts: string[] = [];
  const day = card.weekday !== undefined ? p.weekdays[card.weekday] : '';
  if (card.recurrence === 'weekly' && card.weekday !== undefined)
    parts.push(p.everyWeekday[card.weekday]);
  else if (card.recurrence === 'weekly') parts.push(p.weekly);
  else if (card.recurrence === 'daily') parts.push(p.daily);
  else if (card.recurrence === 'monthly') parts.push(p.monthly);
  else if (card.relative === 'next' && day) parts.push(p.nextWeekday.replace('{d}', day));
  else if (day) parts.push(p.oneWeekday.replace('{d}', day));
  else if (card.relative === 'today') parts.push(p.today);
  else if (card.relative === 'tomorrow') parts.push(p.tomorrow);
  else if (card.relative === 'tonight') parts.push(p.tonight);
  if (card.time !== undefined) parts.push(p.at.replace('{t}', formatTime(card.time, locale)));
  return parts.join(' ');
}

/* The full meta line under a title: when, amount, assignee, quantity, dot-separated. */
export function cardMeta(card: CardSeed, locale: Locale): string {
  const p = PHRASES[locale];
  const parts: string[] = [];
  const when = cardWhen(card, locale);
  if (when) parts.push(when);
  if (card.amount !== undefined) parts.push(formatAmount(card.amount, locale));
  if (card.quantity !== undefined) parts.push(`x${card.quantity}`);
  if (card.assignee) parts.push(p.assignee.replace('{n}', card.assignee));
  return parts.join(' · ');
}

/* What the inline edit field starts with, for the card's key argument. */
export function keyArgValue(card: CardSeed, locale: Locale): string {
  switch (card.keyArg) {
    case 'amount':
      return card.amount !== undefined ? String(card.amount) : '';
    case 'assignee':
      return card.assignee || '';
    case 'when':
      return cardWhen(card, locale);
    default:
      return cardTitle(card, locale);
  }
}

/* Apply a typed edit to the key argument. Returns the fields that change. */
export function applyEdit(card: CardSeed, value: string): Partial<CardSeed> {
  const typed = value.trim();
  if (!typed) return {};
  switch (card.keyArg) {
    case 'amount': {
      const amount = Number(typed.replace(/[^\d.,]/g, '').replace(',', '.'));
      return Number.isNaN(amount) ? {} : { amount };
    }
    case 'assignee':
      return { assignee: typed.charAt(0).toUpperCase() + typed.slice(1) };
    case 'when': {
      const w = readWhenText(typed);
      if (w.weekday !== undefined) {
        return {
          weekday: w.weekday,
          relative: w.relative,
          recurrence: w.relative ? undefined : w.recurrence || card.recurrence,
          time: w.time !== undefined ? w.time : card.time,
          whenText: undefined,
        };
      }
      if (w.relative) {
        return {
          weekday: undefined,
          relative: w.relative,
          recurrence: undefined,
          time: w.time ?? card.time,
          whenText: undefined,
        };
      }
      if (w.recurrence) {
        return {
          weekday: undefined,
          relative: undefined,
          recurrence: w.recurrence,
          time: w.time ?? card.time,
          whenText: undefined,
        };
      }
      if (w.time !== undefined) return { time: w.time, whenText: undefined };
      return { whenText: typed };
    }
    default:
      return { title: typed, titleByLocale: undefined };
  }
}
